import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridColDef,
	GridRenderCellParams,
} from '@mui/x-data-grid';
import {
	ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH,
	ADMIN_SYMBOL_PAGE_SIZE_OPTIONS,
	AdminSymbolPageSize,
	IAdminSymbolDataset,
	IAdminSymbol,
	IAdminSymbolMatrixItem,
	isAdminSymbolPageSize,
	useAdminSymbolMatrixQuery,
} from '../../api/admin';
import SymbolDetailsDialog from './SymbolDetailsDialog';

const COMPANY_COLUMN_WIDTH = 290;
const DATASET_COLUMN_WIDTH = 48;
const DATASET_HEADER_HEIGHT = 270;
const COMPANY_SEARCH_DEBOUNCE_MS = 200;

function CompanyHeaderSearch({
	value,
	onSearchChange,
}: {
	value: string;
	onSearchChange: (value: string) => void;
}) {
	const [inputValue, setInputValue] = React.useState(value);

	React.useEffect(() => {
		setInputValue(value);
	}, [value]);

	React.useEffect(() => {
		if (inputValue === value) return undefined;
		const timeout = window.setTimeout(
			() => onSearchChange(inputValue),
			COMPANY_SEARCH_DEBOUNCE_MS,
		);
		return () => window.clearTimeout(timeout);
	}, [inputValue, onSearchChange, value]);

	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, lineHeight: 'normal' }}>
			<Typography variant="caption" fontWeight={600} lineHeight={1.2}>
				Company
			</Typography>
			<TextField
				placeholder="Search companies"
				value={inputValue}
				onChange={(event) => setInputValue(event.target.value)}
				onClick={(event) => event.stopPropagation()}
				onKeyDown={(event) => event.stopPropagation()}
				type="search"
				size="small"
				fullWidth
				inputProps={{
					'aria-label': 'Search companies',
					maxLength: ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH,
				}}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon fontSize="small" />
						</InputAdornment>
					),
				}}
				sx={{
					'& .MuiInputBase-input': {
						py: 0.75,
						fontSize: 12,
					},
				}}
			/>
		</Box>
	);
}

interface Props {
	selectedDatasets: IAdminSymbolDataset[];
	companySearch: string;
	onCompanySearchChange: (value: string) => void;
	page: number;
	pageSize: AdminSymbolPageSize;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: AdminSymbolPageSize) => void;
}

function getCompanyMapping(company: IAdminSymbolMatrixItem) {
	for (const key in company.values) {
		const mappings = company.values[key];
		if (mappings && mappings.length > 0) return mappings[0];
	}
	return null;
}

function getCompanyLabel(company: IAdminSymbolMatrixItem) {
	const mapping = getCompanyMapping(company);
	const mappedSymbol = mapping?.company_symbol?.trim() || company.company_bloomberg_ticker?.trim();
	const name = company.company_name?.trim() || mapping?.company_name?.trim();
	const primary = mappedSymbol && name
		? `${mappedSymbol} | ${name}`
		: mappedSymbol || name || 'Unknown company';
	const parentName = mapping?.parent_company_id != null
		? mapping.parent_company_name?.trim()
		: '';
	return `${primary}${parentName ? ` (${parentName})` : ''}`;
}

function CompanyCell({ company }: { company: IAdminSymbolMatrixItem }) {
	const label = getCompanyLabel(company);
	return (
		<Tooltip title={label} placement="top-start">
			<Typography
				component="span"
				variant="caption"
				tabIndex={0}
				aria-label={label}
				noWrap
				sx={{
					display: 'block',
					width: '100%',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				}}
			>
				{label}
			</Typography>
		</Tooltip>
	);
}


function MappingCell({
	mappings,
	onOpen,
}: {
	mappings: IAdminSymbol[] | null;
	onOpen: (symbols: IAdminSymbol[]) => void;
}) {
	if (!mappings || mappings.length === 0) {
		return <Box component="span" aria-label="No mappings" />;
	}

	const symbolCount = mappings.length;
	return (
		<Tooltip title={`View ${symbolCount} symbol${symbolCount === 1 ? '' : 's'}`}>
			<Box
				component="button"
				type="button"
				onClick={(event) => {
					event.stopPropagation();
					onOpen(mappings);
				}}
				aria-label={`View details for ${symbolCount} mapped symbol${symbolCount === 1 ? '' : 's'}`}
				sx={{
					display: 'inline-grid',
					placeItems: 'center',
					width: 20,
					height: 20,
					p: 0,
					border: 0,
					borderRadius: '50%',
					bgcolor: 'success.main',
					color: 'success.contrastText',
					cursor: 'pointer',
					fontFamily: 'inherit',
					fontSize: symbolCount === 1 ? 14 : 11,
					fontWeight: 900,
					lineHeight: 1,
					'&:hover': {
						bgcolor: 'success.dark',
					},
					'&:focus-visible': {
						outline: '2px solid',
						outlineColor: 'primary.main',
						outlineOffset: 2,
					},
				}}
			>
				{symbolCount === 1 ? '✓' : symbolCount}
			</Box>
		</Tooltip>
	);
}

function EmptySelection() {
	return (
		<Stack minHeight={400} alignItems="center" justifyContent="center" spacing={0.5} textAlign="center">
			<Typography variant="subtitle2">Select datasets to build a matrix</Typography>
			<Typography variant="body2" color="text.secondary">
				Choose one or more datasets above to compare each company&apos;s symbol mappings side by side.
			</Typography>
		</Stack>
	);
}

function MatrixNoRowsOverlay() {
	return (
		<Stack height="100%" alignItems="center" justifyContent="center" spacing={0.5} textAlign="center">
			<Typography variant="body2">No companies found</Typography>
			<Typography variant="caption" color="text.secondary">
				The selected datasets do not contain company symbol mappings.
			</Typography>
		</Stack>
	);
}

export default function SymbolMatrixTable({
	selectedDatasets,
	companySearch,
	onCompanySearchChange,
	page,
	pageSize,
	onPageChange,
	onPageSizeChange,
}: Props) {
	const [selectedSymbols, setSelectedSymbols] = React.useState<IAdminSymbol[]>([]);
	const datasetIds = React.useMemo(
		() => selectedDatasets.map((dataset) => dataset.id),
		[selectedDatasets],
	);
	const { data, isLoading, isFetching, isError, refetch } = useAdminSymbolMatrixQuery(
		datasetIds,
		page,
		pageSize,
		companySearch,
	);

	const responseMatchesSelection = data !== undefined
		&& data.columns.length === datasetIds.length
		&& data.columns.every((column, index) => column.dataset_id === datasetIds[index]);
	const matrixData = responseMatchesSelection ? data : undefined;

	const datasetById = React.useMemo(
		() => new Map(selectedDatasets.map((dataset) => [dataset.id, dataset])),
		[selectedDatasets],
	);
	const handleOpenSymbolDetails = React.useCallback((symbols: IAdminSymbol[]) => {
		setSelectedSymbols(symbols);
	}, []);

	const columns = React.useMemo<GridColDef<IAdminSymbolMatrixItem>[]>(() => {
		const companyColumn: GridColDef<IAdminSymbolMatrixItem> = {
			field: 'company_name',
			headerName: 'Company',
			renderHeader: () => (
				<CompanyHeaderSearch
					value={companySearch}
					onSearchChange={onCompanySearchChange}
				/>
			),
			width: COMPANY_COLUMN_WIDTH,
			minWidth: COMPANY_COLUMN_WIDTH,
			headerClassName: 'symbol-matrix-company-header',
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			valueGetter: (params) => getCompanyLabel(params.row),
			renderCell: (
				params: GridRenderCellParams<string, IAdminSymbolMatrixItem>,
			) => <CompanyCell company={params.row} />,
		};

		const datasetColumns = (matrixData?.columns ?? []).map<GridColDef<IAdminSymbolMatrixItem>>((column) => {
			const dataset = datasetById.get(column.dataset_id);
			const datasetLabel = dataset
				? `${dataset.vendor_name} / ${dataset.name}`
				: `Dataset ${column.dataset_id}`;

			return {
				field: column.key,
				headerName: `${datasetLabel} (ID ${column.dataset_id})`,
				description: `${datasetLabel}, dataset ID ${column.dataset_id}`,
				width: DATASET_COLUMN_WIDTH,
				minWidth: DATASET_COLUMN_WIDTH,
				align: 'center',
				headerAlign: 'center',
				headerClassName: 'symbol-matrix-dataset-header',
				cellClassName: 'symbol-matrix-status-cell',
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				valueGetter: (params) => params.row.values[column.key] ?? null,
				renderHeader: () => (
					<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
						<Box
							sx={{
								position: 'absolute',
								right: 0,
								bottom: 28,
								left: 0,
								display: 'flex',
								justifyContent: 'center',
							}}
						>
							<Typography
								variant="caption"
								fontSize={11}
								fontWeight={600}
								noWrap
								sx={{
									writingMode: 'vertical-rl',
									transform: 'rotate(180deg)',
								}}
							>
								{datasetLabel}
							</Typography>
						</Box>
						<Typography
							fontSize={11}
							variant="caption"
							color="text.secondary"
							sx={{ position: 'absolute', right: 0, bottom: 4, left: 0, textAlign: 'center' }}
						>
							{column.dataset_id}
						</Typography>
					</Box>
				),
				renderCell: (
					params: GridRenderCellParams<IAdminSymbol[] | null, IAdminSymbolMatrixItem>,
				) => (
					<MappingCell
						mappings={params.value ?? null}
						onOpen={handleOpenSymbolDetails}
					/>
				),
			};
		});

		return [companyColumn, ...datasetColumns];
	}, [
		companySearch,
		datasetById,
		handleOpenSymbolDetails,
		matrixData?.columns,
		onCompanySearchChange,
	]);

	const MatrixErrorOverlay = React.useCallback(() => (
		<Stack height="100%" alignItems="center" justifyContent="center" spacing={1} textAlign="center">
			<Typography variant="body2" color="error.main">Symbol matrix could not be loaded</Typography>
			<Typography variant="caption" color="text.secondary">
				The server could not return mappings for this dataset selection.
			</Typography>
			<Button size="small" color="inherit" onClick={() => { void refetch(); }}>
				Try again
			</Button>
		</Stack>
	), [refetch]);

	if (selectedDatasets.length === 0) {
		return <EmptySelection />;
	}

	return (
		<>
			<Box sx={{ height: 'calc(100vh - 280px)', minHeight: 400, width: '100%', minWidth: 0 }}>
				<DataGrid
					aria-label="Company symbol mapping matrix"
					rows={matrixData?.items ?? []}
					columns={columns}
					getRowId={(row) => row.company_id}
					getRowClassName={(params) => (
						params.indexRelativeToCurrentPage % 2 === 1 ? 'symbol-matrix-row-alternate' : ''
					)}
					rowCount={matrixData?.total ?? 0}
					loading={isLoading || isFetching || (!isError && !responseMatchesSelection)}
					error={isError}
					pagination
					paginationMode="server"
					page={page - 1}
					pageSize={pageSize}
					rowsPerPageOptions={[...ADMIN_SYMBOL_PAGE_SIZE_OPTIONS]}
					onPageChange={(zeroBasedPage) => onPageChange(zeroBasedPage + 1)}
					onPageSizeChange={(nextPageSize) => {
						if (isAdminSymbolPageSize(nextPageSize)) onPageSizeChange(nextPageSize);
					}}
					disableColumnFilter
					disableSelectionOnClick
					rowHeight={34}
					headerHeight={DATASET_HEADER_HEIGHT}
					components={{
						NoRowsOverlay: MatrixNoRowsOverlay,
						ErrorOverlay: MatrixErrorOverlay,
					}}
					sx={{
						'& .MuiDataGrid-cell': {
							minWidth: 0,
						},
						'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
							borderRight: '1px solid',
							borderColor: 'divider',
						},
						'& .symbol-matrix-row-alternate': {
							backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.025),
						},
						'& .symbol-matrix-company-header .MuiDataGrid-columnHeaderTitleContainer': {
							width: '100%',
							alignItems: 'flex-end',
							p: 0.5,
						},
						'& .symbol-matrix-company-header .MuiDataGrid-columnHeaderTitleContainerContent': {
							width: '100%',
						},
						'& .symbol-matrix-dataset-header': {
							overflow: 'visible',
						},
						'& .symbol-matrix-dataset-header .MuiDataGrid-columnHeaderTitleContainer': {
							width: '100%',
							height: '100%',
							p: 0,
							overflow: 'visible',
						},
						'& .symbol-matrix-dataset-header .MuiDataGrid-columnHeaderTitleContainerContent': {
							width: '100%',
							height: '100%',
						},
						'& .symbol-matrix-status-cell': {
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							p: '2px !important',
						},
						'& .MuiDataGrid-virtualScroller': {
							overflowX: 'auto',
						},
					}}
				/>
			</Box>
			<SymbolDetailsDialog
				open={selectedSymbols.length > 0}
				symbols={selectedSymbols}
				onClose={() => setSelectedSymbols([])}
			/>
		</>
	);
}
